<template>
  <div>
    <div class="gg-idrawer__search">
      <input
        v-model="search"
        type="text"
        class="gg-idrawer__search-input"
        placeholder="Digite para buscar templates ou cenas"
        aria-label="Buscar templates ou cenas"
        @keyup.enter="runSearch"
      />
      <button type="button" class="gg-idrawer__search-icon" aria-label="Buscar" @click="runSearch">
        <v-icon size="20">mdi-magnify</v-icon>
      </button>
    </div>

    <div class="gg-idrawer__counter">
      <span class="gg-idrawer__count">
        {{ totalResults }} disponíve{{ totalResults === 1 ? 'l' : 'is' }}
      </span>
      <span class="gg-idrawer__selected">
        <span class="gg-idrawer__count">
          {{ selected.length }} selecionado{{ selected.length === 1 ? '' : 's' }}
        </span>
        <button
          v-if="selected.length"
          type="button"
          class="gg-idrawer__clear"
          aria-label="Limpar seleção"
          @click="$emit('update:selected', [])"
        >
          <v-icon size="20">mdi-close-circle</v-icon>
        </button>
      </span>
    </div>

    <div class="gg-idrawer__grid">
      <label
        v-for="tmpl in entries"
        :key="tmpl.id"
        class="gg-idrawer__item"
        :class="{ 'gg-idrawer__item--selected': isSelected(tmpl) }"
      >
        <input
          type="checkbox"
          class="gg-idrawer__control"
          :checked="isSelected(tmpl)"
          @change="toggle(tmpl)"
        />
        <span class="gg-idrawer__thumb">
          <img
            v-if="!failedThumbs.includes(tmpl.id)"
            :src="thumbUrl(tmpl.id)"
            :alt="tmpl.title"
            @error="failedThumbs = [...failedThumbs, tmpl.id]"
          />
          <v-icon v-else size="32" class="gg-picker__thumb-fallback">mdi-cube-outline</v-icon>
        </span>
        <span class="gg-idrawer__label">{{ tmpl.title }}</span>
      </label>
    </div>

    <div v-if="busy" class="gg-idrawer__loading">
      <v-progress-circular indeterminate size="24" color="primary" />
    </div>

    <div v-else-if="loadError" class="sc-empty-state">
      <span class="sc-empty-state__title">{{ loadError }}</span>
      <button type="button" class="gg-idrawer__retry" @click="retry">Tentar novamente</button>
    </div>

    <div v-else-if="!entries.length" class="sc-empty-state">
      <span class="sc-empty-state__title">
        {{ appliedSearch ? 'Nenhum template encontrado' : 'Nenhum template Vizrt disponível' }}
      </span>
    </div>
  </div>
</template>

<script>
/**
 * The Vizrt template catalog as a multi-select grid. The catalog runs to
 * hundreds of entries, so pages are appended as the *host drawer's* body
 * scrolls — the host owns the scroll container and calls `maybeLoadMore`.
 *
 * Paging follows the API's keyset cursor rather than a page number: the API
 * holds the catalog per pod, so an offset walked across replicas drifts and
 * silently skips templates mid-scroll.
 */
import { mapActions } from 'vuex'
import { getVizrtThumbnailUrl } from '@/store/modules/globographics'

const PAGE_SIZE = 25
// Distance from the bottom at which the next page is requested.
const SCROLL_THRESHOLD_PX = 240

export default {
  name: 'GloboGraphicsTemplatePicker',
  props: {
    // Selected template objects, two-way via `:selected.sync`.
    selected: { type: Array, default: () => [] },
  },
  data() {
    return {
      entries: [],
      cursor: null,
      reachedEnd: false,
      totalResults: 0,
      search: '',
      appliedSearch: '',
      failedThumbs: [],
      loadError: null,
      // This picker's OWN in-flight state. It used to read a module-wide loading
      // flag that fetchVizrtTemplateModel also set — and the inline editor triggers
      // that whenever the on-air item changes, from a Stream Deck take or a MOS
      // sync, with no interaction here. So an unrelated on-air change replaced this
      // drawer's error message and its retry button with a spinner mid-read. Keep
      // any future loading flag scoped to the thing that is actually loading.
      busy: false,
      // Monotonic request id — see loadPage.
      loadSeq: 0,
    }
  },
  computed: {
    hasMore() {
      // A failed page stops the scroll handler; the retry button reopens it.
      return !this.reachedEnd && !this.loadError
    },
  },
  methods: {
    ...mapActions({ fetchVizrtTemplates: 'globographics/fetchVizrtTemplates' }),
    thumbUrl: getVizrtThumbnailUrl,
    isSelected(tmpl) {
      return this.selected.some((t) => t.id === tmpl.id)
    },
    toggle(tmpl) {
      const next = this.isSelected(tmpl)
        ? this.selected.filter((t) => t.id !== tmpl.id)
        : [...this.selected, tmpl]
      this.$emit('update:selected', next)
    },
    runSearch() {
      this.appliedSearch = this.search.trim()
      this.reload()
    },
    async reload() {
      this.entries = []
      this.cursor = null
      this.reachedEnd = false
      this.totalResults = 0
      this.failedThumbs = []
      this.loadError = null
      await this.loadPage()
    },
    // Clears the error so hasMore opens back up, then re-requests the same page.
    async retry() {
      this.loadError = null
      await this.loadPage()
    },
    async loadPage() {
      // Sequence-guarded, because `busy` alone cannot fix this: reload() (from a
      // search) does not check it, and it is a boolean — the first response to land
      // clears it while the second is still running. A search fired mid-scroll would
      // otherwise clear entries, then have the older UNFILTERED page resolve and
      // append itself into the now search-scoped list, overwriting cursor/reachedEnd
      // so further scrolling pages through the wrong result set.
      const seq = ++this.loadSeq
      let data
      this.busy = true
      try {
        // The action's return value is the only source here. The catalog is paged
        // independently by several drawers, so holding it in a shared store slot
        // meant one drawer could render a page another had fetched.
        data = (await this.fetchVizrtTemplates({
          after: this.cursor,
          size: PAGE_SIZE,
          q: this.appliedSearch || '',
        })) || {}
      } catch (error) {
        // Leave entries and cursor untouched so an already-loaded list stays
        // usable, and stop the scroll handler re-firing the same failing request.
        if (seq !== this.loadSeq) return
        this.loadError = error.response?.data?.error || 'Erro ao carregar templates Vizrt'
        return
      } finally {
        if (seq === this.loadSeq) this.busy = false
      }
      // A superseded page must not touch entries, cursor or the counter.
      if (seq !== this.loadSeq) return
      const fetched = data.entries || []
      // Guard against a page arriving twice (scroll can fire faster than the request).
      const known = new Set(this.entries.map((t) => t.id))
      this.entries = [...this.entries, ...fetched.filter((t) => !known.has(t.id))]
      this.totalResults = data.totalResults || this.entries.length
      this.cursor = data.nextCursor || null
      // The API returns a null cursor exactly at the end of the catalog.
      this.reachedEnd = !this.cursor
    },
    // Called by the host drawer on its body scroll.
    async maybeLoadMore(el) {
      if (this.busy || !this.hasMore) return
      if (el.scrollHeight - el.scrollTop - el.clientHeight > SCROLL_THRESHOLD_PX) return
      await this.loadPage()
    },
  },
}
</script>

<style lang="scss" scoped>
// Search field — matches the panel's own search input.
.gg-idrawer__search {
  position: relative;
  display: flex;
  align-items: center;
  height: 56px;
  padding: 0 var(--sc-space-3);
  background: var(--sc-gg-surface-2);
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
}

.gg-idrawer__search-input {
  flex: 1 1 auto;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: var(--sc-gg-text-primary);
  font-size: 16px;
  line-height: 24px;

  &::placeholder {
    color: var(--sc-gg-text-tertiary);
  }
}

.gg-idrawer__search-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;

  .v-icon {
    color: var(--sc-gg-text-secondary) !important;
  }
}

.gg-picker__thumb-fallback {
  color: var(--sc-gg-text-tertiary) !important;
}
</style>
